import { useEffect, useRef } from 'react';
import type * as React from 'react';
import type { NavigateFunction } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import { DEFAULT_TOOL_SCOPES } from '../constants';
import { buildCreateMessageRequest, resolveComposerParentMessageId } from '../utils/composer';
import { sortConversationMessagesForDisplay } from '../utils/conversationThread';
import { compareConversationMessages } from '../utils/conversationRunActivity';
import type {
  ApprovalRequest,
  AutopilotMode,
  Conversation,
  ConversationAttachment,
  ConversationAppSecret,
  ConversationAutopilotSettings,
  ConversationMessage,
  CreateConversationAppSecretRequest,
  CreateMessageResponse,
  MergeRequestSummary,
  OpsSecret,
  QueueItem,
  ToolTokenResponse,
  UpdateMessageResponse
} from '../types';

type Args = {
  apiClient: ApiClient;
  navigate: NavigateFunction;
  conversationId: string;
  isArchived: boolean;
  holdQueuedMessages: boolean;
  selectedModelId: string | null;
  orderedQueuedMessageIds: string[];
  orderedQueue: QueueItem[];
  queueReorderBusy: boolean;
  approvalBusy: boolean;
  editingBusy: boolean;
  editingTitleBusy: boolean;
  toolTokenBusy: boolean;
  canManageTools: boolean;
  gitlabBusy: boolean;
  autopilotBusy: boolean;
  autopilotMode: AutopilotMode;
  conversation: Conversation | null;
  messages: ConversationMessage[];
  pendingAttachments: ConversationAttachment[];
  inputValue: string;
  editingContent: string;
  editingMessageId: string | null;
  editingTitleValue: string;
  toolTokenValue: string | null;
  toolTokenStatusActiveScopes: string[] | null;
  toolTokenScopes: string[];
  conversationSecretAppId: string;
  conversationOpsSecrets: OpsSecret[];
  conversationSecretDraftKey: string;
  conversationSecretDraftType: 'env' | 'file';
  conversationSecretDraftValue: string;
  conversationSecretDraftDescription: string;
  approvalRequest: ApprovalRequest | null;
  approvalResponseValue: string;
  streamingRunId: string | null;
  streamingRunApprovalFifo: string | null;
  onMessageSent?: (conversationId: string) => void;
  setError: (message: string | null) => void;
  reportErrorLocal: (
    context: string,
    error: unknown,
    setError: (message: string | null) => void,
    setLocalError: (message: string | null) => void
  ) => void;
  captureConversationStateVersion: () => number;
  isCurrentConversationRequest: (requestConversationId: string, requestVersion: number) => boolean;
  loadConversation: () => Promise<void>;
  loadConversationAutopilot: () => Promise<void>;
  loadQueue: () => Promise<void>;
  loadRuns: () => Promise<void>;
  loadToolTokenStatus: () => Promise<void>;
  loadConversationSecrets: () => Promise<void>;
  loadGitlabInfo: () => Promise<void>;
  loadMessages: (force?: boolean) => Promise<void>;
  setConversation: React.Dispatch<React.SetStateAction<Conversation | null>>;
  setMessages: React.Dispatch<React.SetStateAction<ConversationMessage[]>>;
  setQueue: React.Dispatch<React.SetStateAction<QueueItem[]>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setPendingAttachments: React.Dispatch<React.SetStateAction<ConversationAttachment[]>>;
  setSending: React.Dispatch<React.SetStateAction<boolean>>;
  setApprovalBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setApprovalDismissedKey: React.Dispatch<React.SetStateAction<string | null>>;
  setEditingMessageId: React.Dispatch<React.SetStateAction<string | null>>;
  setEditingContent: React.Dispatch<React.SetStateAction<string>>;
  setEditingBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingTitle: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingTitleValue: React.Dispatch<React.SetStateAction<string>>;
  setEditingTitleBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setConversationError: React.Dispatch<React.SetStateAction<string | null>>;
  setGitlabBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setAutopilotBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setAutopilotMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setToolTokenBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setToolTokenValue: React.Dispatch<React.SetStateAction<string | null>>;
  setToolTokenStatus: React.Dispatch<React.SetStateAction<any>>;
  setToolTokenScopes: React.Dispatch<React.SetStateAction<string[]>>;
  setShowToolAccess: React.Dispatch<React.SetStateAction<boolean>>;
  setConversationSecretsBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setConversationSecretsError: React.Dispatch<React.SetStateAction<string | null>>;
  setConversationSecretDraftValue: React.Dispatch<React.SetStateAction<string>>;
  setQueueReorderBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setShellAutoExpandMessageId: React.Dispatch<React.SetStateAction<string | null>>;
};

export function useConversationPageActions({
  apiClient,
  navigate,
  conversationId,
  isArchived,
  holdQueuedMessages,
  selectedModelId,
  orderedQueuedMessageIds,
  orderedQueue,
  queueReorderBusy,
  approvalBusy,
  editingBusy,
  editingTitleBusy,
  toolTokenBusy,
  canManageTools,
  gitlabBusy,
  autopilotBusy,
  autopilotMode,
  conversation,
  messages,
  pendingAttachments,
  inputValue,
  editingContent,
  editingMessageId,
  editingTitleValue,
  toolTokenValue,
  toolTokenStatusActiveScopes,
  toolTokenScopes,
  conversationSecretAppId,
  conversationOpsSecrets,
  conversationSecretDraftKey,
  conversationSecretDraftType,
  conversationSecretDraftValue,
  conversationSecretDraftDescription,
  approvalRequest,
  approvalResponseValue,
  streamingRunId,
  streamingRunApprovalFifo,
  onMessageSent,
  setError,
  reportErrorLocal,
  captureConversationStateVersion,
  isCurrentConversationRequest,
  loadConversation,
  loadConversationAutopilot,
  loadQueue,
  loadRuns,
  loadToolTokenStatus,
  loadConversationSecrets,
  loadGitlabInfo,
  loadMessages,
  setConversation,
  setMessages,
  setQueue,
  setInputValue,
  setPendingAttachments,
  setSending,
  setApprovalBusy,
  setApprovalDismissedKey,
  setEditingMessageId,
  setEditingContent,
  setEditingBusy,
  setEditingTitle,
  setEditingTitleValue,
  setEditingTitleBusy,
  setConversationError,
  setGitlabBusy,
  setAutopilotBusy,
  setAutopilotMenuOpen,
  setToolTokenBusy,
  setToolTokenValue,
  setToolTokenStatus,
  setToolTokenScopes,
  setShowToolAccess,
  setConversationSecretsBusy,
  setConversationSecretsError,
  setConversationSecretDraftValue,
  setQueueReorderBusy,
  setShellAutoExpandMessageId
}: Args) {
  const sendQueueRef = useRef<Promise<void>>(Promise.resolve());
  const queuedParentMessageIdRef = useRef<string | null>(null);
  const pendingSendCountRef = useRef(0);
  const messagesRef = useRef<ConversationMessage[]>(messages);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    sendQueueRef.current = Promise.resolve();
    queuedParentMessageIdRef.current = null;
    pendingSendCountRef.current = 0;
    setSending(false);
    setApprovalBusy(false);
    setEditingBusy(false);
    setEditingTitleBusy(false);
    setGitlabBusy(false);
    setAutopilotBusy(false);
    setToolTokenBusy(false);
    setConversationSecretsBusy(false);
  }, [
    conversationId,
    setApprovalBusy,
    setAutopilotBusy,
    setConversationSecretsBusy,
    setEditingBusy,
    setEditingTitleBusy,
    setGitlabBusy,
    setSending,
    setToolTokenBusy
  ]);

  async function toggleArchive() {
    if (!conversation) return;
    setConversationError(null);
    try {
      if (isArchived) {
        const updated = await apiClient.post<Conversation>(
          `/api/conversations/${conversationId}/unarchive`,
          {}
        );
        setConversation(updated);
      } else {
        const updated = await apiClient.post<Conversation>(
          `/api/conversations/${conversationId}/archive`,
          {}
        );
        setConversation(updated);
      }
    } catch (err) {
      reportErrorLocal(
        isArchived ? 'Failed to unarchive conversation' : 'Failed to archive conversation',
        err,
        setError,
        setConversationError
      );
    }
  }

  async function sendMessage(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    const content = inputValue.trim();
    if (!content && pendingAttachments.length === 0) {
      setError('Message or file required');
      return;
    }
    const isShellCommand = content.startsWith('!');
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId) {
      return;
    }
    const queueMode = holdQueuedMessages;
    const modelId = selectedModelId;
    pendingSendCountRef.current += 1;
    setSending(true);
    const runSubmission = async () => {
      try {
        if (isArchived) {
          const updated = await apiClient.post<Conversation>(
            `/api/conversations/${requestConversationId}/unarchive`,
            {}
          );
          if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
            return;
          }
          setConversation(updated);
        }
        const parentMessageId =
          queuedParentMessageIdRef.current ??
          resolveComposerParentMessageId(messagesRef.current, orderedQueuedMessageIds);
        const response = await apiClient.post<CreateMessageResponse>(
          `/api/conversations/${requestConversationId}/messages`,
          buildCreateMessageRequest({
            content,
            holdQueuedMessages: queueMode,
            modelId,
            parentMessageId,
            attachmentIds: pendingAttachments.map((attachment) => attachment.id)
          })
        );
        queuedParentMessageIdRef.current = response.message.id;
        if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
          return;
        }
        onMessageSent?.(requestConversationId);
        setInputValue('');
        setPendingAttachments([]);
        setMessages((prev) => {
          const merged = new Map(prev.map((message) => [message.id, message]));
          merged.set(response.message.id, response.message);
          return Array.from(merged.values()).sort(compareConversationMessages);
        });
        if (response.queue_item) {
          setQueue((prev) => {
            const exists = prev.some((item) => item.id === response.queue_item!.id);
            if (exists) {
              return prev.map((item) =>
                item.id === response.queue_item!.id ? response.queue_item! : item
              );
            }
            return [...prev, response.queue_item!];
          });
        }
        setConversation((prev) =>
          prev ? { ...prev, status: response.conversation_status as Conversation['status'] } : prev
        );
        if (isShellCommand) {
          setShellAutoExpandMessageId(response.message.id);
        }
        await Promise.all([
          loadQueue(),
          loadRuns(),
          loadConversation(),
          loadToolTokenStatus(),
          loadConversationAutopilot()
        ]);
      } catch (err) {
        queuedParentMessageIdRef.current = resolveComposerParentMessageId(
          messagesRef.current,
          orderedQueuedMessageIds
        );
        if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
          return;
        }
        reportErrorLocal('Failed to send message', err, setError, setConversationError);
      } finally {
        pendingSendCountRef.current = Math.max(0, pendingSendCountRef.current - 1);
        if (pendingSendCountRef.current === 0) {
          queuedParentMessageIdRef.current = null;
          if (isCurrentConversationRequest(requestConversationId, requestVersion)) {
            setSending(false);
          }
        }
      }
    };

    sendQueueRef.current = sendQueueRef.current.then(runSubmission, runSubmission);
    await sendQueueRef.current;
  }

  async function updateAutopilotMode(nextMode: AutopilotMode) {
    if (autopilotBusy || nextMode === autopilotMode) {
      setAutopilotMenuOpen(false);
      return;
    }
    setAutopilotBusy(true);
    try {
      const data = await apiClient.patch<ConversationAutopilotSettings>(
        `/api/conversations/${conversationId}/autopilot`,
        { mode: nextMode }
      );
      setAutopilotMenuOpen(false);
      setConversation((prev) => prev);
      void data;
      await loadConversationAutopilot();
    } catch (err) {
      reportErrorLocal('Failed to update autopilot mode', err, setError, setConversationError);
    } finally {
      setAutopilotBusy(false);
    }
  }

  function startEditing(message: ConversationMessage) {
    setEditingMessageId(message.id);
    setEditingContent(message.content);
  }

  function cancelEditing() {
    setEditingMessageId(null);
    setEditingContent('');
  }

  async function saveEditedMessage(messageId: string) {
    if (editingBusy) {
      return;
    }
    const content = editingContent.trim();
    if (!content) {
      setError('Message required');
      return;
    }
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId) {
      return;
    }
    const originalMessage = messages.find((msg) => msg.id === messageId);
    setEditingBusy(true);
    try {
      const response = await apiClient.patch<UpdateMessageResponse>(
        `/api/conversations/${requestConversationId}/messages/${messageId}`,
        {
          content,
          auto_process: !holdQueuedMessages,
          model_id: originalMessage?.model_id ?? undefined
        }
      );
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      setMessages((prev) => prev.map((msg) => (msg.id === messageId ? response.message : msg)));
      if (response.queue_item) {
        setQueue((prev) => {
          const exists = prev.some((item) => item.id === response.queue_item!.id);
          if (exists) {
            return prev.map((item) =>
              item.id === response.queue_item!.id ? response.queue_item! : item
            );
          }
          return [...prev, response.queue_item!];
        });
        if (conversation && response.conversation_status === 'queued') {
          setConversation({ ...conversation, status: 'queued' });
        }
      }

      setEditingMessageId(null);
      setEditingContent('');
      await Promise.all([loadRuns(), loadToolTokenStatus(), loadConversationAutopilot()]);
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to update message', err, setError, setConversationError);
    } finally {
      if (isCurrentConversationRequest(requestConversationId, requestVersion)) {
        setEditingBusy(false);
      }
    }
  }

  async function saveEditedTitle() {
    if (editingTitleBusy) {
      return;
    }
    const title = editingTitleValue.trim();
    if (!title) {
      setError('Title required');
      return;
    }
    setEditingTitleBusy(true);
    try {
      const data = await apiClient.patch<Conversation>(`/api/conversations/${conversationId}`, {
        title
      });
      setConversation(data);
      setEditingTitle(false);
      setEditingTitleValue('');
    } catch (err) {
      reportErrorLocal('Failed to update title', err, setError, setConversationError);
    } finally {
      setEditingTitleBusy(false);
    }
  }

  async function createToolToken(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    if (toolTokenBusy || !canManageTools) {
      return;
    }
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId) {
      return;
    }
    setToolTokenBusy(true);
    setToolTokenValue(null);
    try {
      const data = await apiClient.post<ToolTokenResponse>(
        `/api/conversations/${requestConversationId}/tool-token`,
        { scopes: toolTokenScopes }
      );
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      setToolTokenValue(data.token);
      setToolTokenStatus({
        status: 'active',
        active: true,
        expires_at: data.expires_at,
        scopes: data.scopes,
        created_at: new Date().toISOString()
      });
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to create tool token', err, setError, setConversationError);
    } finally {
      if (isCurrentConversationRequest(requestConversationId, requestVersion)) {
        setToolTokenBusy(false);
      }
    }
  }

  async function revokeToolToken() {
    if (toolTokenBusy) {
      return;
    }
    setToolTokenBusy(true);
    try {
      await apiClient.del(`/api/conversations/${conversationId}/tool-token`);
      setToolTokenValue(null);
      await loadToolTokenStatus();
    } catch (err) {
      reportErrorLocal('Failed to revoke tool token', err, setError, setConversationError);
    } finally {
      setToolTokenBusy(false);
    }
  }

  function openToolAccess() {
    if (toolTokenStatusActiveScopes && toolTokenStatusActiveScopes.length > 0) {
      setToolTokenScopes(toolTokenStatusActiveScopes);
    } else {
      setToolTokenScopes(DEFAULT_TOOL_SCOPES);
    }
    setShowToolAccess(true);
  }

  async function copyToolToken() {
    if (!toolTokenValue) {
      return;
    }
    try {
      await navigator.clipboard.writeText(toolTokenValue);
    } catch (err) {
      reportErrorLocal('Failed to copy tool token', err, setError, setConversationError);
    }
  }

  async function copyMessageContent(content: string) {
    try {
      await navigator.clipboard.writeText(content);
    } catch (err) {
      reportErrorLocal('Failed to copy message', err, setError, setConversationError);
    }
  }

  async function cancelQueue(itemId: string) {
    try {
      await apiClient.post(`/api/conversations/${conversationId}/queue/${itemId}/cancel`);
      await loadQueue();
    } catch (err) {
      reportErrorLocal('Failed to cancel queue item', err, setError, setConversationError);
    }
  }

  async function allowPipelineWait(itemId: string) {
    try {
      await apiClient.post(
        `/api/conversations/${conversationId}/queue/${itemId}/pipeline-wait/allow`
      );
      await Promise.all([loadMessages(true), loadQueue()]);
    } catch (err) {
      reportErrorLocal('Failed to allow pipeline wait', err, setError, setConversationError);
    }
  }

  async function stopPipelineWait(itemId: string) {
    try {
      await apiClient.post(
        `/api/conversations/${conversationId}/queue/${itemId}/pipeline-wait/stop`
      );
      await Promise.all([loadMessages(true), loadQueue()]);
    } catch (err) {
      reportErrorLocal('Failed to stop pipeline wait', err, setError, setConversationError);
    }
  }

  async function startQueuedMessage(itemId: string) {
    try {
      await apiClient.patch(`/api/conversations/${conversationId}/queue/${itemId}`, {
        start_now: true
      });
      await loadQueue();
    } catch (err) {
      reportErrorLocal('Failed to start queued item', err, setError, setConversationError);
    }
  }

  async function toggleQueuedMessageHold(itemId: string, manualHold: boolean) {
    try {
      await apiClient.patch(`/api/conversations/${conversationId}/queue/${itemId}`, {
        manual_hold: manualHold
      });
      await loadQueue();
    } catch (err) {
      reportErrorLocal('Failed to update queue hold', err, setError, setConversationError);
    }
  }

  async function moveQueuedMessage(messageId: string, direction: 'up' | 'down') {
    if (queueReorderBusy) {
      return;
    }
    const index = orderedQueue.findIndex((item) => item.message_id === messageId);
    if (index < 0) {
      return;
    }
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= orderedQueue.length) {
      return;
    }
    const nextOrder = [...orderedQueue];
    [nextOrder[index], nextOrder[swapIndex]] = [nextOrder[swapIndex], nextOrder[index]];
    setQueueReorderBusy(true);
    try {
      await apiClient.post(`/api/conversations/${conversationId}/queue/reorder`, {
        ordered_message_ids: nextOrder.map((item) => item.message_id)
      });
      await Promise.all([loadQueue(), loadMessages(true)]);
      setMessages((prev) =>
        sortConversationMessagesForDisplay(prev, nextOrder.map((item) => item.message_id))
      );
    } catch (err) {
      reportErrorLocal('Failed to reorder queue', err, setError, setConversationError);
    } finally {
      setQueueReorderBusy(false);
    }
  }

  async function submitApproval(payload: { decision?: 'approve' | 'deny'; response?: string }) {
    if (!streamingRunId || !streamingRunApprovalFifo || approvalBusy) {
      return;
    }
    setApprovalBusy(true);
    try {
      await apiClient.post(
        `/api/conversations/${conversationId}/runs/${streamingRunId}/approvals`,
        payload
      );
      if (approvalRequest) {
        const key = approvalRequest.id ?? approvalRequest.raw ?? null;
        if (key) {
          setApprovalDismissedKey(key);
        }
      }
    } catch (err) {
      reportErrorLocal('Failed to send approval decision', err, setError, setConversationError);
    } finally {
      setApprovalBusy(false);
    }
  }

  async function createMergeRequest() {
    if (!conversationId || gitlabBusy) {
      return;
    }
    setGitlabBusy(true);
    try {
      await apiClient.post<{ merge_request: MergeRequestSummary; created: boolean }>(
        `/api/conversations/${conversationId}/merge-request`,
        {}
      );
      await loadGitlabInfo();
    } catch (err) {
      reportErrorLocal('Failed to create merge request', err, setError, setConversationError);
    } finally {
      setGitlabBusy(false);
    }
  }

  async function mergeMergeRequest(squashOnMerge: boolean) {
    if (!conversationId || gitlabBusy) {
      return;
    }
    setGitlabBusy(true);
    try {
      await apiClient.post<{ merge_request: MergeRequestSummary; created: boolean }>(
        `/api/conversations/${conversationId}/merge-request/merge`,
        { squash: squashOnMerge }
      );
      await apiClient.post<Conversation>(`/api/conversations/${conversationId}/archive`, {});
      navigate('/conversations', {
        state: {
          notification: {
            type: 'success',
            message: `Merge request merged${squashOnMerge ? ' with squash' : ''} and conversation archived.`,
            link: {
              to: `/conversations/${conversationId}`,
              label: 'View conversation'
            }
          }
        }
      });
    } catch (err) {
      reportErrorLocal('Failed to merge merge request', err, setError, setConversationError);
    } finally {
      setGitlabBusy(false);
    }
  }

  async function saveConversationSecretOverride() {
    if (!conversationSecretAppId || !conversationSecretDraftKey.trim()) {
      return;
    }
    const spec = conversationOpsSecrets.find((secret) => secret.key === conversationSecretDraftKey);
    const secretType = (spec?.secret_type ?? conversationSecretDraftType ?? 'env') as 'env' | 'file';
    if (!conversationSecretDraftValue.trim()) {
      setConversationSecretsError('Value is required');
      return;
    }
    if (secretType === 'file' && !(spec?.file_path ?? '').trim()) {
      setConversationSecretsError('File path must be configured in deploy/ops.yaml for file-type secrets');
      return;
    }
    setConversationSecretsBusy(true);
    setConversationSecretsError(null);
    try {
      const payload: CreateConversationAppSecretRequest = {
        app_id: conversationSecretAppId,
        key: conversationSecretDraftKey,
        value: conversationSecretDraftValue,
        secret_type: secretType,
        description: conversationSecretDraftDescription.trim() || undefined
      };
      await apiClient.post(`/api/conversations/${conversationId}/secrets`, payload);
      setConversationSecretDraftValue('');
      await loadConversationSecrets();
    } catch (err) {
      reportErrorLocal('Failed to save conversation secret override', err, setError, setConversationError);
      setConversationSecretsError(err instanceof Error ? err.message : String(err));
    } finally {
      setConversationSecretsBusy(false);
    }
  }

  async function resetConversationSecretOverride(secretId: string) {
    if (!secretId) {
      return;
    }
    setConversationSecretsBusy(true);
    setConversationSecretsError(null);
    try {
      await apiClient.del(`/api/conversations/${conversationId}/secrets/${secretId}`);
      await loadConversationSecrets();
    } catch (err) {
      reportErrorLocal('Failed to reset conversation secret override', err, setError, setConversationError);
      setConversationSecretsError(err instanceof Error ? err.message : String(err));
    } finally {
      setConversationSecretsBusy(false);
    }
  }

  return {
    toggleArchive,
    sendMessage,
    updateAutopilotMode,
    startEditing,
    cancelEditing,
    saveEditedMessage,
    saveEditedTitle,
    createToolToken,
    revokeToolToken,
    openToolAccess,
    copyToolToken,
    copyMessageContent,
    cancelQueue,
    allowPipelineWait,
    stopPipelineWait,
    startQueuedMessage,
    toggleQueuedMessageHold,
    moveQueuedMessage,
    submitApproval,
    createMergeRequest,
    mergeMergeRequest,
    saveConversationSecretOverride,
    resetConversationSecretOverride
  };
}
